import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import Login from './pages/Login';
import Layout from './components/Layout';
import Dashboard from './pages/Dashboard';
import Schools from './pages/Schools';
import SchoolDetails from './pages/SchoolDetails';
import Users from './pages/Users';
import Payments from './pages/Payments';
import Logs from './pages/Logs';
import { authService, ErrorBoundary } from '@uniqcamp/shared';

const theme = createTheme({
  palette: {
    primary: { main: '#4f227d', light: '#7b4da8', dark: '#371755', contrastText: '#fff' },
    secondary: { main: '#ffe200', light: '#ffeb3d', dark: '#e6cb00', contrastText: '#1a1a1a' },
    background: { default: '#f8f6fa', paper: '#ffffff' },
  },
  typography: {
    fontFamily: '"Inter", "Segoe UI", "Roboto", sans-serif',
    h4: { fontWeight: 700 },
    h5: { fontWeight: 600 },
    h6: { fontWeight: 600 },
    button: { fontWeight: 600, textTransform: 'none' },
  },
  shape: { borderRadius: 0 },
  components: {
    MuiButton: {
      styleOverrides: {
        root: { minHeight: 44, textTransform: 'none', fontWeight: 600, '@media (min-width: 600px)': { minHeight: 36 } },
      },
    },
    MuiIconButton: { styleOverrides: { root: { minWidth: 48, minHeight: 48 } } },
    MuiPaper: { styleOverrides: { root: { boxShadow: '0 2px 12px rgba(79, 34, 125, 0.08)' } } },
    MuiTextField: { defaultProps: { variant: 'outlined', size: 'small' } },
    MuiChip: { styleOverrides: { root: {} } },
    MuiAppBar: {
      styleOverrides: {
        root: { background: 'linear-gradient(135deg, #4f227d 0%, #371755 100%)', boxShadow: '0 2px 12px rgba(79, 34, 125, 0.25)' },
      },
    },
  },
});

const PrivateRoute = ({ children }: { children: React.ReactNode }): React.ReactElement | null => {
  const isAuthenticated = authService.isAuthenticated();
  const user = authService.getStoredUser();

  if (!isAuthenticated || !user || user.role !== 'SUPER_ADMIN') {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
};

function App() {
  return (
    <ErrorBoundary>
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Router>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route
            path="/"
            element={
              <PrivateRoute>
                <Layout />
              </PrivateRoute>
            }
          >
            <Route index element={<Dashboard />} />
            <Route path="schools" element={<Schools />} />
            <Route path="schools/:schoolId" element={<SchoolDetails />} />
            <Route path="users" element={<Users />} />
            <Route path="payments" element={<Payments />} />
            <Route path="logs" element={<Logs />} />
          </Route>
        </Routes>
      </Router>
    </ThemeProvider>
    </ErrorBoundary>
  );
}

export default App;
